import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import DeleteIcon from '@mui/icons-material/Delete';
import MailIcon from '@mui/icons-material/Mail';
import OverrideIcon from '@mui/icons-material/NewReleases';
import {
    Box,
    Button,
    Divider,
    Fab,
    FormHelperText,
    Grid,
    IconButton,
    Tooltip,
    Typography
} from '@mui/material';
import { useEffect, useMemo } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import * as yup from 'yup';
import { FormAutocomplete, FormCheckbox, FormTextField } from '../../../../common/form';
import {
    CreateCustomerRequest,
    CustomerDto,
    CustomerEmailDto,
    DayOfWeek,
    EntityTypes,
    ExternalPricelistDto,
    PricelistMailAttachmentType,
    PricelistMailLayoutType
} from '../../../../common/PricePrgClient';
import { WeekdayChooser } from '../../../../common/WeekdayChooser';
import { RootState } from '../../../../store';
import CreateContactDialog from '../../../contacts/CreateContactDialog';
import { setShowCreateContactDialog } from '../../../contacts/redux/contactsSlices';
import { getAppSettings } from '../../../settings/redux/settingsSelectors';
import { getSettingsUi } from '../../CustomerSelector';
import SendPricelistToCustomerDialog from '../../dialogs/SendPricelistToCustomerDialog';
import SendPricelistToMailDialog from '../../dialogs/SendPricelistToMailDialog';
import { PricelistMailAttachmentTypeChooser } from '../../PricelistMailAttachmentTypeChooser';
import { getCustomerUi, getSelectedCustomer } from '../../redux/customerSelectors';
import {
    showSendPricelistToCustomerDialog,
    showSendPricelistToEmailDialog,
    updateCustomer
} from '../../redux/customerSlices';
import { MailSendoutChooser } from './MailSendoutChooser';
import { LatestPricelistMailSendoutInfo } from '../../LatestPricelistMailSendoutInfo';

const mapStateToProps = (state: RootState) => {
    const settingsUi = getSettingsUi(state);
    const appSettings = getAppSettings(state);

    return {
        selectedCustomer: getSelectedCustomer(state),
        externalPricelists: state.externalPricelist.externalPricelists,
        isMailDialogOpen: getCustomerUi(state).isSendPricelistToCustomerDialogOpen,
        settableWeekdays: appSettings.pricelistMailingWeekdays,
        availableMailSendouts: appSettings.mailSendouts,
        isSendPricelistToEmailDialogOpen: settingsUi.isSendPricelistToEmailDialogOpen,
        appRestrictions: state.settings.applicationRestrictions,
        contacts: state.contacts.contacts,
        showCreateContactDialog: state.contacts.ui.showCreateContactDialog,
        languages: appSettings.availableLanguages
    };
};

type Props = ReturnType<typeof mapStateToProps>;

type SaveCustomerForm = Omit<
    CreateCustomerRequest,
    'externalPricelistId' | 'customerType' | 'responsible' | 'parentCustomerId' | 'contactId' | 'language'
> & {
    parentCustomerId?: number | null;
    responsible?: string | null;
    externalPricelistId?: number | undefined;
    customerEmails: CustomerEmailDto[];
    mailAttachmentTypes: PricelistMailAttachmentType[];
    hasPricelistMailWeekdayRestrictions: boolean;
    allowedPricelistMailWeekdays: DayOfWeek[];
    contactId?: number | undefined | null;
    language?: string | null;
    showValidToOnPricelistMail: boolean;
    useDetailedPricelistMailLayout: boolean;
};

const schema: yup.SchemaOf<SaveCustomerForm> = yup.object().shape({
    name: yup.string().required().max(60).label('Name'),
    customerNumber: yup.number().optional().label('Description'),
    parentCustomerId: yup.number().nullable().optional().label('Parent customer id'),
    externalPricelistId: yup.mixed().optional().label('External pricelist'),
    city: yup.string().required().label('City'),
    responsible: yup.string().nullable().optional().label('Responsible'),
    language: yup.string().nullable().optional().label('Language'),
    customerEmails: yup.array().of(
        yup.object().shape({
            email: yup.string().email().required().label('Email'),
            mailSendoutIds: yup.array().of(yup.string().required())
        })
    ),
    mailAttachmentTypes: yup.array(),
    hasPricelistMailWeekdayRestrictions: yup.boolean().required(),
    allowedPricelistMailWeekdays: yup
        .array(
            yup
                .mixed<DayOfWeek>()
                .oneOf([
                    DayOfWeek.Monday,
                    DayOfWeek.Tuesday,
                    DayOfWeek.Wednesday,
                    DayOfWeek.Thursday,
                    DayOfWeek.Friday,
                    DayOfWeek.Saturday,
                    DayOfWeek.Sunday
                ])
                .required()
        )
        .optional()
        .default([]),
    contactId: yup.number().nullable().optional().label('Responsible'),
    showValidToOnPricelistMail: yup
        .boolean()
        .required()
        .default(false)
        .label('Show valid to on pricelist mail'),
    useDetailedPricelistMailLayout: yup
        .boolean()
        .required()
        .default(false)
        .label('Use detailed pricelist mail layout')
});

function getSettableWeekdays(customer: CustomerDto | null | undefined, settableWeekdays: DayOfWeek[]) {
    if (
        customer == null ||
        customer.hasPricelistMailWeekdayRestrictions !== true ||
        customer.allowedPricelistMailWeekdays == null
    ) {
        return settableWeekdays;
    }

    return settableWeekdays.filter(w => customer.allowedPricelistMailWeekdays?.includes(w));
}

function GeneralCustomerTab({
    selectedCustomer,
    externalPricelists,
    isMailDialogOpen,
    settableWeekdays,
    isSendPricelistToEmailDialogOpen,
    availableMailSendouts,
    appRestrictions,
    contacts,
    showCreateContactDialog,
    languages
}: Props) {
    const dispatch = useDispatch();

    const {
        control,
        reset,
        handleSubmit,
        getValues,
        formState: { isDirty }
    } = useForm<SaveCustomerForm>({
        defaultValues: useMemo(
            () => ({
                customerNumber: selectedCustomer?.customerNumber,
                name: selectedCustomer?.name,
                city: selectedCustomer?.city,
                parentCustomerId: selectedCustomer?.parentCustomerId,
                responsible: selectedCustomer?.responsible,
                customerEmails: selectedCustomer?.customerEmails ?? [],
                externalPricelistId: selectedCustomer?.externalPricelistId,
                mailAttachmentTypes: selectedCustomer?.pricelistMailAttachmentTypes ?? [],
                allowedPricelistMailWeekdays: getSettableWeekdays(selectedCustomer, settableWeekdays),
                hasPricelistMailWeekdayRestrictions: selectedCustomer?.hasPricelistMailWeekdayRestrictions,
                contactId: selectedCustomer?.contactId,
                language: selectedCustomer?.language ?? '',
                showValidToOnPricelistMail: selectedCustomer?.showValidToOnPricelistMail ?? false,
                useDetailedPricelistMailLayout:
                    selectedCustomer?.pricelistMailLayoutType == PricelistMailLayoutType.Detailed
            }),
            [selectedCustomer, settableWeekdays]
        ),
        resolver: yupResolver(schema)
    });
    const { fields, append, remove } = useFieldArray({ control, name: 'customerEmails' });

    useEffect(() => {
        if (selectedCustomer != null) {
            reset({
                customerNumber: selectedCustomer?.customerNumber,
                name: selectedCustomer?.name,
                city: selectedCustomer?.city,
                parentCustomerId: selectedCustomer?.parentCustomerId,
                responsible: selectedCustomer?.responsible,
                customerEmails: selectedCustomer.customerEmails,
                externalPricelistId: selectedCustomer.externalPricelistId,
                mailAttachmentTypes: selectedCustomer.pricelistMailAttachmentTypes,
                allowedPricelistMailWeekdays: getSettableWeekdays(selectedCustomer, settableWeekdays),
                hasPricelistMailWeekdayRestrictions: selectedCustomer.hasPricelistMailWeekdayRestrictions,
                contactId: selectedCustomer.contactId,
                language: selectedCustomer.language,
                showValidToOnPricelistMail: selectedCustomer.showValidToOnPricelistMail,
                useDetailedPricelistMailLayout:
                    selectedCustomer.pricelistMailLayoutType === PricelistMailLayoutType.Detailed
            });
        }
    }, [selectedCustomer, settableWeekdays, reset]);

    if (selectedCustomer == null) {
        return null;
    }

    const canEdit = !appRestrictions[EntityTypes.Customer]?.isCreateDisabled;
    const canCreateContact = !appRestrictions[EntityTypes.Contact]?.isCreateDisabled;
    const submitForm = (form: SaveCustomerForm) => {
        dispatch(
            updateCustomer.request({
                name: form.name,
                city: form.city,
                customerNumber: form.customerNumber,
                externalPricelistId: form.externalPricelistId,
                customerEmails: form.customerEmails ?? [],
                customerId: selectedCustomer.customerId,
                pricelistMailAttachmentTypes: form.mailAttachmentTypes,
                allowedPricelistMailWeekdays: form.allowedPricelistMailWeekdays ?? [],
                hasPricelistMailWeekdayRestrictions: form.hasPricelistMailWeekdayRestrictions,
                contactId: form.contactId ?? undefined,
                language: form.language ?? undefined,
                showValidToOnPricelistMail: form.showValidToOnPricelistMail ?? false,
                pricelistMailLayoutType: form.useDetailedPricelistMailLayout
                    ? PricelistMailLayoutType.Detailed
                    : PricelistMailLayoutType.Standard
            })
        );
    };

    const handleAddEmailField = () => {
        append({
            email: '',
            mailSendoutIds: availableMailSendouts.length === 1 ? [availableMailSendouts[0].mailSendoutId] : []
        });
    };

    const handleRemoveEmailField = (index: number) => {
        remove(index);
    };

    const handleOpenSendPricelistsToCustomersDialog = () => {
        dispatch(showSendPricelistToCustomerDialog(true));
    };

    const handleShowSendPricelistToMailDialog = (open: boolean) => {
        dispatch(showSendPricelistToEmailDialog(open));
    };

    const handleCreateContact = () => {
        dispatch(setShowCreateContactDialog(true));
    };
    return (
        <>
            <form onSubmit={handleSubmit(submitForm)}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={12}>
                        <Grid container direction="row" justifyContent="space-between">
                            <Typography component="h1" variant="h5">
                                {selectedCustomer.customerNumber} - {selectedCustomer.name}
                            </Typography>
                            <Grid item sx={theme => ({ marginRight: theme.spacing(1) })}>
                                <Fab
                                    size="medium"
                                    color="secondary"
                                    sx={theme => ({
                                        marginRight: theme.spacing(2)
                                    })}
                                    disabled={
                                        selectedCustomer.customerEmails.flatMap(e => e.mailSendoutIds)
                                            .length === 0
                                    }
                                    onClick={handleOpenSendPricelistsToCustomersDialog}
                                >
                                    <Tooltip title="Send pricelist to customer">
                                        <ContactMailIcon />
                                    </Tooltip>
                                </Fab>
                                <Fab
                                    size="medium"
                                    color="secondary"
                                    sx={theme => ({
                                        marginRight: theme.spacing(2)
                                    })}
                                    onClick={() => handleShowSendPricelistToMailDialog(true)}
                                >
                                    <Tooltip title="Send pricelist to email">
                                        <MailIcon />
                                    </Tooltip>
                                </Fab>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Grid container direction="row" spacing={2}>
                            <Grid item xs={12} md={12} lg={4}>
                                <FormTextField
                                    control={control}
                                    name="customerNumber"
                                    disabled={!canEdit}
                                    label="Customer number"
                                    value={selectedCustomer.customerNumber ?? ''}
                                    variant="standard"
                                />
                            </Grid>
                            <Grid item xs={12} md={12} lg={4}>
                                <FormTextField
                                    name="name"
                                    control={control}
                                    disabled={!canEdit}
                                    label="Name"
                                    value={selectedCustomer.name}
                                    variant="standard"
                                />
                            </Grid>
                            <Grid item xs={12} md={12} lg={4}>
                                <FormAutocomplete
                                    disabled={!canEdit}
                                    name="externalPricelistId"
                                    control={control}
                                    defaultValue={selectedCustomer?.externalPricelistId}
                                    label="External Pricelist"
                                    getOptionLabel={pricelist => {
                                        if (!pricelist) {
                                            return '';
                                        }

                                        return readablePricelistName(externalPricelists[pricelist]);
                                    }}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    options={Object.values(externalPricelists).map(
                                        c => c.externalPricelistId
                                    )}
                                    clearOnEscape
                                    style={{ width: 300 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={12} lg={4}>
                                <FormTextField
                                    control={control}
                                    name="city"
                                    label="City"
                                    disabled={!canEdit}
                                    value={selectedCustomer.city ?? ' '}
                                    variant="standard"
                                />
                            </Grid>
                            <Grid item xs={12} md={12} lg={4}>
                                <FormAutocomplete
                                    sx={{ width: '200px', display: 'inline-flex' }}
                                    control={control}
                                    name="contactId"
                                    label="Responsible"
                                    options={Object.values(contacts).map(c => c.contactId)}
                                    getOptionLabel={c => contacts[c].fullName}
                                    defaultValue={null}
                                />
                                <Tooltip sx={{ display: 'inline-flex' }} title="Add contact">
                                    <Fab
                                        sx={{ display: 'inline-flex', marginLeft: 2 }}
                                        size="small"
                                        color="primary"
                                        disabled={!canCreateContact}
                                        onClick={handleCreateContact}
                                    >
                                        <AddIcon />
                                    </Fab>
                                </Tooltip>
                            </Grid>
                            <Grid item xs={12} md={12} lg={4}>
                                <Box sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
                                    <FormAutocomplete
                                        sx={{ width: '120px', display: 'inline-flex' }}
                                        control={control}
                                        name="language"
                                        label="Language"
                                        options={languages}
                                    />
                                    {selectedCustomer.language !== selectedCustomer.erpLanguage && (
                                        <Tooltip
                                            title={'Language overriden from: ' + selectedCustomer.erpLanguage}
                                        >
                                            <OverrideIcon sx={{ color: 'primary.main' }}></OverrideIcon>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={12} lg={12}>
                                <Divider />
                            </Grid>
                            {fields.map((field, idx) => (
                                <Grid item key={field.id} xs={12}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} md={12} lg={5}>
                                            <FormTextField
                                                id={field.id}
                                                control={control}
                                                name={`customerEmails.${idx}.email`}
                                                label="Email"
                                                fullWidth
                                                InputProps={{
                                                    autoComplete: 'none'
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12} lg={3}>
                                            <Controller
                                                name={`customerEmails.${idx}.mailSendoutIds`}
                                                control={control}
                                                defaultValue={
                                                    availableMailSendouts.length === 1
                                                        ? [availableMailSendouts[0].mailSendoutId]
                                                        : []
                                                }
                                                render={({ field: { onChange, value } }) => (
                                                    <MailSendoutChooser
                                                        availableMailSendouts={availableMailSendouts}
                                                        selectedMailSendoutIds={value}
                                                        setSelectedMailSendoutIds={ids => onChange(ids)}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12} lg={1}>
                                            <Tooltip title="Remove email">
                                                <IconButton
                                                    onClick={() => handleRemoveEmailField(idx)}
                                                    color="secondary"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            ))}
                            <Grid item xs={12} md={12} lg={12}>
                                <Button
                                    onClick={handleAddEmailField}
                                    color="primary"
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                >
                                    Add email
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={12} lg={12}>
                        <Divider />
                    </Grid>
                    <Grid item sx={theme => ({ padding: `0 ${theme.spacing(2)}` })} xs={12}>
                        <Typography variant="subtitle2">Pricelist mail attachments</Typography>
                        <Controller
                            name="mailAttachmentTypes"
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <div>
                                    <PricelistMailAttachmentTypeChooser
                                        mailAttachmentTypes={value}
                                        setMailAttachmentTypes={mat => onChange(mat)}
                                    />

                                    {error != null && (
                                        <FormHelperText error={true}>{error.message}</FormHelperText>
                                    )}
                                </div>
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Tooltip title="Select this if you want 'Valid from' and 'Valid to' to be visible on the pricelist mail.">
                            <span>
                                <FormCheckbox
                                    control={control}
                                    name="showValidToOnPricelistMail"
                                    label="Show pricelist dates on pricelist mail"
                                />
                            </span>
                        </Tooltip>
                    </Grid>
                    <Grid item xs={12}>
                        <FormCheckbox
                            label="Use detailed pricelist mail layout"
                            name="useDetailedPricelistMailLayout"
                            control={control}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Tooltip title="Select this if you only want this customer to receive pricelist mails on specific weekdays.">
                            <span>
                                <FormCheckbox
                                    control={control}
                                    name="hasPricelistMailWeekdayRestrictions"
                                    label="Send pricelists on specific weekdays"
                                />
                            </span>
                        </Tooltip>
                    </Grid>
                    <Grid item xs={12}>
                        <Controller
                            name="allowedPricelistMailWeekdays"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <WeekdayChooser
                                    selectedWeekdays={value}
                                    setSelectedWeekdays={days => onChange(days)}
                                    disabled={!getValues('hasPricelistMailWeekdayRestrictions')}
                                    disableWeekday={weekDay => !settableWeekdays.includes(weekDay)}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <LatestPricelistMailSendoutInfo
                                sendoutResult={selectedCustomer.latestPricelistMailSendoutResult?.toCustomer}
                            />
                            <LatestPricelistMailSendoutInfo
                                sendoutResult={selectedCustomer.latestPricelistMailSendoutResult?.toEmail}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={12} lg={12}>
                        <Divider />
                    </Grid>
                    <Grid item>
                        <Button type="submit" color="primary" variant="contained" disabled={!isDirty}>
                            Save
                        </Button>
                    </Grid>
                </Grid>
            </form>

            {isMailDialogOpen && <SendPricelistToCustomerDialog customer={selectedCustomer} />}

            {isSendPricelistToEmailDialogOpen && (
                <SendPricelistToMailDialog
                    selectedCustomer={selectedCustomer}
                    closeDialog={() => handleShowSendPricelistToMailDialog(false)}
                />
            )}

            {showCreateContactDialog && <CreateContactDialog />}
        </>
    );
}

function readablePricelistName(extPricelist: ExternalPricelistDto) {
    return `${extPricelist?.externalPricelistRef} - ${extPricelist?.name}`;
}

export default connect(mapStateToProps)(GeneralCustomerTab);
